import { CabinetWrapper } from '@/shared/cabinet-wrapper';
import UITabs from '@/shared/ui/ui-tabs';
import { Pagination, Stack } from '@mui/material';
import { RequestsList } from '../OutgoingRequestsPage/ui/requests-list';
import { useGetIncomingDealsQuery } from '@/app/api';

import { useTranslation } from 'react-i18next';
import useFilters from '@/app/hooks/useFilters';
import { useFactoringTypeFilter } from '@/app/hooks/useFactoringTypeFilter';
import { EmptyDataPlaceholderProps } from '../../shared/ui/empty-data-placeholder';
import { UITabItemLabel } from '../../shared/ui/ui-table/ui/tab-item-label';
import { PageTitle } from '@/shared/page-title';
import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import { CURRENCY_OPTIONS } from '@/constants';
import { Currency } from '@/app/api/types';

const arrOfStatuses = ['', 'contractCreated', 'completed', 'contractRejected'];

export const IncomingRequestsPage = () => {
  const { t } = useTranslation();

  const factoringTypeOptions = [
    {
      label: t('factoring_types.all'),
      value: 'all',
      filterValue: undefined,
    },
    {
      label: t('factoring_types.opened'),
      value: 'opened',
      filterValue: 'opened',
    },
    // {
    //   label: t('factoring_types.closed'),
    //   value: 'closed',
    //   filterValue: 'closed',
    // },
    {
      label: t('factoring_types.reverse'),
      value: 'reverse',
      filterValue: 'reverse',
    },
  ];

  const { filters, updateFilters } = useFilters({
    saveToUrl: true,
    defaultFilters: {
      page: 1,
      pageSize: 20,
      tab: 0,
      factoringType: 'all',
      currency: 'UZS',
    },
  });

  const factoringType = useFactoringTypeFilter(
    filters.factoringType,
    (value) => updateFilters({ factoringType: value })
  );

  const { data, isLoading } = useGetIncomingDealsQuery({
    ...filters,
    status: arrOfStatuses[filters.tab],
    page: Number(filters.page),
    pageSize: Number(filters.pageSize),
    factoringType: factoringTypeOptions.find(
      (item) => item.value === factoringType
    )?.filterValue,
    currency: filters.currency as Currency,
  });

  const title = t('currently_no_requests');
  const subtitle = t('currently_no_requests');
  const hasAnyDoc = data?.data.length;
  const emptyPlaceholderProps: EmptyDataPlaceholderProps = {
    title,
    subtitle,
    isLoading,
    isEmpty: !hasAnyDoc,
  };

  const labels = [
    {
      label: t('incoming_requests_tabs.all'),
      count:
        (data?.dealsCount?.completed || 0) +
        (data?.dealsCount?.contractCreated || 0) +
        (data?.dealsCount?.contractRejected || 0),
    },
    {
      label: t('incoming_requests_tabs.waiting_for_sign'),
      count: data?.dealsCount?.contractCreated || 0,
    },
    {
      label: t('incoming_requests_tabs.successfull'),
      count: data?.dealsCount?.completed || 0,
    },
    {
      label: t('incoming_requests_tabs.rejected'),
      count: data?.dealsCount?.contractRejected || 0,
    },
  ];

  const tabs = labels.map((item) => ({
    label: <UITabItemLabel label={item.label} count={item.count} />,
    component: (
      <>
        <Stack direction='row' spacing={2} mb={2}>
          <UIAutocomplete
            value={factoringTypeOptions.find(
              (item) => item.value === factoringType
            )}
            options={factoringTypeOptions}
            getOptionLabel={(option) => option.label}
            onChange={(_e, value) =>
              updateFilters({ factoringType: value?.value })
            }
            TextFieldProps={{
              label: t('factoring_type'),
              sx: { width: 240 },
            }}
            disableClearable
          />
          <UIAutocomplete
            value={CURRENCY_OPTIONS.find(
              (item) => item.value === filters.currency
            )}
            options={CURRENCY_OPTIONS}
            TextFieldProps={{
              label: t('currency'),
              sx: { width: 240 },
            }}
            getOptionLabel={(option) => {
              return t(option.label);
            }}
            onChange={(_event, value) => {
              updateFilters({ currency: value?.value as Currency });
            }}
            disableClearable
          />
        </Stack>

        <RequestsList
          data={data?.data || []}
          emptyPlaceholderProps={emptyPlaceholderProps}
          type='buyer'
        />
      </>
    ),
  }));

  return (
    <CabinetWrapper>
      <PageTitle title={t('my_requests')} noBack />
      <UITabs
        value={Number(filters.tab || 0)}
        onChange={(_e, value) => updateFilters({ tab: value, page: 1 })}
        sx={{
          mt: 4,
        }}
        tabs={tabs}
      />

      {!!hasAnyDoc && (
        <Pagination
          count={data?.totalPages}
          page={Number(filters.page)}
          onChange={(_, page) => updateFilters({ page })}
        />
      )}
    </CabinetWrapper>
  );
};
